/**
 * Gate 1 — Step 2: Risk Register.
 *
 * Lazy by design — does NOT hit the network until the parent says it's unlocked
 * (i.e. Step 1 has generated weights AND the total = 100%). The first render
 * after unlock fires GET /risk-register; later mounts read from local state.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  fetchRiskRegister,
  generateRiskRegister,
  addRisk,
  updateRisk,
  deleteRisk,
} from "../../services/riskRegisterClient";
import AddRiskDialog from "./AddRiskDialog";
import DeactivateRiskDialog from "./DeactivateRiskDialog";
import DeleteRiskDialog from "./DeleteRiskDialog";
import EditRiskDialog from "./EditRiskDialog";
import ReactivateRiskDialog from "./ReactivateRiskDialog";
import { postGate0FeaturesEnabled } from "../../config/productFeatures";

const POST_GATE0_DISABLED = !postGate0FeaturesEnabled();
const log = (...args) => console.log("[GATE1.STEP2.UI]", ...args);

/** Backend source → Figma label */
function sourceLabel(source) {
  return source === "Generated" ? "Canonical" : "Derived";
}

function isCanonical(risk) {
  return risk.source === "Generated";
}

const SEVERITY_DOT = {
  Critical: "var(--Danger-60, #B82500)",
  High: "var(--Danger-40, #FF7152)",
  Medium: "var(--Warning-60, #8A5C00)",
  Low: "var(--Success-60, #00754E)",
};

const LIKELIHOOD_DOT = {
  High: "var(--Danger-40, #FF7152)",
  Medium: "var(--Warning-60, #8A5C00)",
  Low: "var(--Success-60, #00754E)",
};

function StatusDot({ value, colors }) {
  const color = colors[value] || "#98A2B3";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "17.16px",
          letterSpacing: "0.15px",
          color,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SourceBadge({ source }) {
  const canonical = source === "Generated";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: canonical ? 78 : 62,
        height: 24,
        boxSizing: "border-box",
        px: 1,
        borderRadius: "12px",
        fontSize: canonical ? 14 : 13,
        fontWeight: 700,
        lineHeight: "24px",
        letterSpacing: 0,
        textAlign: "center",
        verticalAlign: "middle",
        bgcolor: canonical ? "#F4EBFF" : "#E0F2FE",
        color: canonical
          ? "var(--Accent-Color-60, #9551EC)"
          : "var(--Primary-Dark, #0046B8)",
      }}
    >
      {sourceLabel(source)}
    </Box>
  );
}

function StatDot({ color, label, count, active, onClick }) {
  const interactive = typeof onClick === "function";
  return (
    <Box
      component={interactive ? "button" : "span"}
      type={interactive ? "button" : undefined}
      onClick={onClick}
      aria-pressed={interactive ? active : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        border: "none",
        bgcolor: "transparent",
        cursor: interactive ? "pointer" : "default",
        p: 0,
        m: 0,
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "20px",
          letterSpacing: 0,
          color: "#555555",
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color }}>
          {count}
        </Box>{" "}
        {label}
      </Typography>
    </Box>
  );
}

const RISK_TABLE_BORDER = "1px solid var(--Grey-20, #E6E6E6)";

const thSx = {
  fontWeight: 700,
  fontSize: "16px",
  color: "var(--Grey-70, #383838)",
  borderBottom: RISK_TABLE_BORDER,
  bgcolor: "transparent",
  py: 1.5,
  px: 1.5,
};

const tdSx = {
  fontSize: 13,
  color: "#344054",
  borderBottom: RISK_TABLE_BORDER,
  py: 1.75,
  px: 1.5,
  verticalAlign: "middle",
};

/** Inset with card content — row lines + left/right rails, equal side margin */
const riskTableWrapSx = {
  width: "100%",
  overflowX: "auto",
  boxSizing: "border-box",
  borderLeft: RISK_TABLE_BORDER,
  borderRight: RISK_TABLE_BORDER,
};

const riskTableSx = {
  width: "100%",
  minWidth: 800,
  borderCollapse: "collapse",
};

export default function Step2RiskRegister({ projectId, unlocked, lockReason, onRisksReady }) {
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const [risks, setRisks] = useState([]);
  const [tab, setTab] = useState("Total");
  const [generating, setGenerating] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deactivating, setDeactivating] = useState(null);
  const [deactivatingBusy, setDeactivatingBusy] = useState(false);
  const [activating, setActivating] = useState(null);
  const [activatingBusy, setActivatingBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  useEffect(() => {
    if (!unlocked) {
      log("locked — skipping GET /risk-register");
      return;
    }
    if (loaded) {
      log("already loaded — no refetch");
      return;
    }
    if (!projectId) return;
    log("unlocked + not loaded → GET /risk-register, projectId:", projectId);
    (async () => {
      const t0 = performance.now();
      try {
        setLoading(true);
        const res = await fetchRiskRegister(projectId);
        if (res?.success) {
          log(
            "✓ risks fetched",
            `${Math.round(performance.now() - t0)}ms`,
            "— riskCount:", res.data.risks?.length || 0,
          );
          setRisks(res.data.risks || []);
          setLoaded(true);
        }
      } catch (e) {
        log("✗ risk fetch threw:", e.message);
        setError(e.response?.data?.error || e.message || "Failed to load risk register.");
      } finally {
        setLoading(false);
      }
    })();
  }, [unlocked, loaded, projectId]);

  const activeRisks = useMemo(
    () => risks.filter((r) => r.enabled !== false),
    [risks]
  );

  useEffect(() => {
    if (typeof onRisksReady !== "function") return;
    const revision = risks
      .map((r) => `${r.id}:${r.enabled !== false ? 1 : 0}`)
      .join("|");
    onRisksReady(unlocked && activeRisks.length > 0, revision);
  }, [unlocked, risks, activeRisks.length, onRisksReady]);

  const deactivatedRisks = useMemo(
    () => risks.filter((r) => r.enabled === false),
    [risks]
  );
  const canonical = useMemo(
    () => risks.filter((r) => r.source === "Generated"),
    [risks]
  );
  const derived = useMemo(
    () => risks.filter((r) => r.source === "Defined"),
    [risks]
  );
  const visible =
    tab === "Canonical"
      ? canonical
      : tab === "Derived"
        ? derived
        : tab === "Deactivated"
          ? deactivatedRisks
          : activeRisks;
  const showRiskTable = !loading && visible.length > 0;

  const handleGenerate = async () => {
    if (!unlocked) return;
    const t0 = performance.now();
    log("user clicked Generate → POST /risk-register/generate");
    try {
      setGenerating(true);
      const res = await generateRiskRegister(projectId);
      if (!res?.success) {
        log("✗ generate failed:", res?.error);
        setError(res?.error || "Risk generation failed.");
        return;
      }
      log(
        "✓ risks generated",
        `${Math.round(performance.now() - t0)}ms`,
        "— generatedCount:", res.data.generatedCount,
      );
      setRisks(res.data.risks || []);
      setLoaded(true);
      setToast(`Generated ${res.data.generatedCount} risks.`);
    } catch (e) {
      log("✗ generate threw:", e.message);
      setError(e.response?.data?.error || e.message || "Risk generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const handleAdd = async (risk) => {
    try {
      const res = await addRisk(projectId, risk);
      if (res?.success) {
        setRisks(res.data.risks || []);
        setAddOpen(false);
        setToast("Risk added.");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to add risk.");
    }
  };

  const handleUpdate = async (patch) => {
    if (!editing?.id) return;
    try {
      const res = await updateRisk(projectId, editing.id, patch);
      if (res?.success) {
        setRisks(res.data.risks || []);
        setEditing(null);
        setToast("Risk updated.");
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to update risk.");
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivating?.id) return;
    const riskName = (deactivating.name || "").trim() || "Risk";
    try {
      setDeactivatingBusy(true);
      const res = await updateRisk(projectId, deactivating.id, { enabled: false });
      if (res?.success) {
        setRisks(res.data.risks || []);
        setDeactivating(null);
        setToast(`${riskName} successfully deactivated`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to deactivate risk.");
    } finally {
      setDeactivatingBusy(false);
    }
  };

  const handleConfirmActivate = async () => {
    if (!activating?.id) return;
    const riskName = (activating.name || "").trim() || "Risk";
    try {
      setActivatingBusy(true);
      const res = await updateRisk(projectId, activating.id, { enabled: true });
      if (res?.success) {
        setRisks(res.data.risks || []);
        setActivating(null);
        setToast(`${riskName} successfully activated`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to activate risk.");
    } finally {
      setActivatingBusy(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleting?.id) return;
    const riskName = (deleting.name || "").trim() || "Risk";
    try {
      setDeletingBusy(true);
      const res = await deleteRisk(projectId, deleting.id);
      if (res?.success) {
        setRisks(res.data.risks || []);
        setDeleting(null);
        setToast(`${riskName} successfully deleted`);
      }
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to delete risk.");
    } finally {
      setDeletingBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid var(--Grey-20, #E6E6E6)",
          borderRadius: 2,
          p: { xs: 2, sm: 3 },
          position: "relative",
          opacity: unlocked ? 1 : 0.55,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        {!unlocked && (
          <Tooltip title={lockReason || "Generate trust attribute weights in Step 1 to unlock Risk Register"}>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.55)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                cursor: "not-allowed",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <LockOutlinedIcon sx={{ fontSize: 32, color: "#888" }} />
                <Typography fontWeight={700} color="#555" mt={1}>
                  {lockReason || "Locked until Step 1 weights are generated"}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        )}

        {/* Header — Figma */}
        <Box
          sx={{
            mx: { xs: -2, sm: -3 },
            px: { xs: 2, sm: 3 },
            pb: 2,
            mb: showRiskTable ? 0 : 2,
            borderBottom: showRiskTable ? "none" : RISK_TABLE_BORDER,
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: "100%",
              letterSpacing: 0,
              color: "#383838",
            }}
          >
            Step 2 — Risk Register
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "21px",
              letterSpacing: 0,
              color: "var(--Grey-60, #555555)",
              mt: 0.5,
            }}
          >
            Generated risks grouped under trust attributes with enterprise extensibility.
          </Typography>

          <Box sx={{ display: "flex", gap: 2.5, mt: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            <StatDot
              color="var(--Grey-50, #707070)"
              label="Total"
              count={activeRisks.length + deactivatedRisks.length}
              active={tab === "Total"}
              onClick={() => setTab("Total")}
            />
            <StatDot
              color="var(--Accent-Color-60, #9551EC)"
              label="Canonical"
              count={canonical.length}
              active={tab === "Canonical"}
              onClick={() => setTab("Canonical")}
            />
            <StatDot
              color="var(--Primary-Dark, #0046B8)"
              label="Derived"
              count={derived.length}
              active={tab === "Derived"}
              onClick={() => setTab("Derived")}
            />
            <StatDot
              color="var(--Success-60, #00754E)"
              label="Active"
              count={activeRisks.length}
              active={tab === "Active"}
              onClick={() => setTab("Active")}
            />
            <StatDot
              color="var(--Warning-60, #8A5C00)"
              label="Deactivated"
              count={deactivatedRisks.length}
              active={tab === "Deactivated"}
              onClick={() => setTab("Deactivated")}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center", color: "#667085" }}>
            {unlocked ? (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: 13, color: "#667085" }}>
                  {tab === "Deactivated"
                    ? "No deactivated risks."
                    : tab === "Active" && risks.length > 0
                      ? "No active risks."
                      : "No risks yet. Generate from trust attributes or add one manually."}
                </Typography>
                {risks.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={handleGenerate}
                  disabled={POST_GATE0_DISABLED || generating}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {generating ? <CircularProgress size={16} /> : "Generate risks"}
                </Button>
                )}
              </Box>
            ) : (
              "Unlock Step 1 to continue."
            )}
          </Box>
        ) : (
          <TableContainer
            sx={{
              ...riskTableWrapSx,
              borderTop: RISK_TABLE_BORDER,
            }}
          >
            <Table size="small" sx={riskTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Risk</TableCell>
                  <TableCell sx={thSx}>Attribute</TableCell>
                  <TableCell sx={thSx}>Severity</TableCell>
                  <TableCell sx={thSx}>Likelihood</TableCell>
                  <TableCell sx={thSx}>Owner</TableCell>
                  <TableCell sx={thSx}>Source</TableCell>
                  <TableCell sx={thSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((r) => {
                  const isActive = r.enabled !== false;
                  return (
                  <TableRow
                    key={r.id}
                    hover
                    sx={!isActive ? { bgcolor: "rgba(138, 92, 0, 0.06)" } : undefined}
                  >
                    <TableCell sx={tdSx}>
                      <Tooltip
                        title={r.description || ""}
                        arrow
                        placement="top-start"
                      >
                        <Typography
                          component="span"
                          sx={{
                            display: "inline-block",
                            fontFamily: "Lato, sans-serif",
                            fontWeight: 700,
                            fontSize: "16px",
                            lineHeight: "20.02px",
                            letterSpacing: "0.15px",
                            color: "var(--Grey-60, #555555)",
                            cursor: "default",
                          }}
                        >
                          {r.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tdSx,
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "18.59px",
                        letterSpacing: "0.14px",
                        color: "var(--Grey-60, #555555)",
                      }}
                    >
                      {r.attribute}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <StatusDot value={r.severity} colors={SEVERITY_DOT} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <StatusDot value={r.likelihood} colors={LIKELIHOOD_DOT} />
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tdSx,
                        fontFamily: "Lato, sans-serif",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "18.59px",
                        letterSpacing: "0.14px",
                        color: "var(--Grey-60, #555555)",
                      }}
                    >
                      {r.owner}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <SourceBadge source={r.source} />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                      {!isActive ? (
                        <Link
                          component="button"
                          type="button"
                          underline="none"
                          disabled={POST_GATE0_DISABLED}
                          onClick={() => setActivating(r)}
                          sx={{
                            fontSize: 13,
                            fontWeight: 700,
                            lineHeight: "24px",
                            letterSpacing: 0,
                            color: "#00754E",
                            verticalAlign: "middle",
                            "&.Mui-disabled": { color: "#98A2B3" },
                          }}
                        >
                          Activate
                        </Link>
                      ) : (
                        <>
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            disabled={POST_GATE0_DISABLED}
                            onClick={() => setEditing(r)}
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              lineHeight: "24px",
                              letterSpacing: 0,
                              color: "#005BF0",
                              mr: 2,
                              verticalAlign: "middle",
                          textUnderlineOffset: "2px",
                          "&:hover:not(.Mui-disabled)": { color: "#005BF0" },
                              "&.Mui-disabled": { color: "#98A2B3", textDecoration: "none" },
                            }}
                          >
                            Edit
                          </Link>
                          {isCanonical(r) ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              disabled={POST_GATE0_DISABLED}
                              onClick={() => setDeactivating(r)}
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: "24px",
                                letterSpacing: 0,
                                color: "#555555",
                                verticalAlign: "middle",
                            textUnderlineOffset: "2px",
                            "&:hover:not(.Mui-disabled)": { color: "#555555" },
                                "&.Mui-disabled": { color: "#98A2B3", textDecoration: "none" },
                              }}
                            >
                              Deactivate
                            </Link>
                          ) : (
                            <Link
                              component="button"
                              type="button"
                              underline="none"
                              disabled={POST_GATE0_DISABLED}
                              onClick={() => setDeleting(r)}
                              sx={{
                                fontSize: 13,
                                fontWeight: 700,
                                lineHeight: "24px",
                                letterSpacing: 0,
                                color: "#DE350B",
                                verticalAlign: "middle",
                                "&.Mui-disabled": { color: "#98A2B3" },
                              }}
                            >
                              Delete
                            </Link>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Bottom action — Figma */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setAddOpen(true)}
            disabled={POST_GATE0_DISABLED || !unlocked}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderColor: "#005BF0",
              color: "#005BF0",
              "&:hover": { borderColor: "#005BF0", bgcolor: "rgba(26,110,245,0.04)" },
            }}
          >
            Add Risk
          </Button>
        </Box>
      </Box>

      <AddRiskDialog open={addOpen} onClose={() => setAddOpen(false)} onSave={handleAdd} />
      <EditRiskDialog
        risk={editing}
        onClose={() => setEditing(null)}
        onSave={handleUpdate}
      />
      <DeactivateRiskDialog
        risk={deactivating}
        submitting={deactivatingBusy}
        onClose={() => {
          if (!deactivatingBusy) setDeactivating(null);
        }}
        onConfirm={handleConfirmDeactivate}
      />
      <ReactivateRiskDialog
        risk={activating}
        submitting={activatingBusy}
        onClose={() => {
          if (!activatingBusy) setActivating(null);
        }}
        onConfirm={handleConfirmActivate}
      />
      <DeleteRiskDialog
        risk={deleting}
        submitting={deletingBusy}
        onClose={() => {
          if (!deletingBusy) setDeleting(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar open={Boolean(error)} onClose={() => setError("")} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            bgcolor: "#000",
            color: "#fff",
            px: 2,
            py: 1.25,
            borderRadius: "8px",
            minWidth: { xs: "auto", sm: 360 },
            maxWidth: "90vw",
            boxShadow: "0 8px 24px rgba(16, 24, 40, 0.28)",
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#12B76A", fontSize: 22, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#fff", flex: 1, lineHeight: 1.4 }}>
            {toast}
          </Typography>
          <IconButton
            size="small"
            aria-label="Dismiss"
            onClick={() => setToast("")}
            sx={{ color: "#fff", p: 0.5, ml: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Snackbar>
    </>
  );
}
